/**
 * Карточка клиента во вкладке «Новый год» (29.10.2026, владелец: «одна карточка на клиента — все его НГ-брони и НГ-заявки,
 * скрины, заметки»). 04.11 (v2, владелец: «нельзя ни удалить заявки, ни понять её статус, предоплату, есть у них квест или нет,
 * ни комментариев нельзя оставить»):
 *   • свёрнута — имя, статус (Новая / В работе / Бронь / Предоплата / Отказ) и плашка предоплаты, телефон, строка квеста
 *     («Квест · дд.мм · чч:мм · игроки» ближайшей брони) или «квеста нет» с пожеланиями заявки, последний комментарий, счётчики;
 *   • раскрыта — «Позвонить», статус (NyStatusBar), брони (тап → сетка + модалка; предоплата; «Убрать из НГ») и заявки
 *     («Обработан», «Удалить» / «Вернуть»; NyRows.tsx), кнопки «Комментарии» (бывшая «История» — открыта сразу), «Скрины», «Инфо».
 */
import { useEffect, useRef, useState } from "react";
import { PlayersBadge } from "../../components/shared";
import { ClientInfoCard } from "../../components/modal/ClientInfoCard";
import { fmtStamp, groupOf, infoTarget, plural, type NyBooking, type NyCard } from "./nyModel";
import { cardPay, ddmm, questLine, wishLine, NY_STATUS } from "./nyStatus";
import { BookingRow, LeadRow, hm } from "./NyRows";
import { NyStatusBar } from "./NyStatusBar";
import { NyNotes } from "./NyNotes";
import { NyShots } from "./NyShots";
import ny from "./dcNy.module.css";

const Chev = () => <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true"><path d="M2 4l4 4 4-4" /></svg>;
const ST_CLS = { new: ny.stNew, work: ny.stWork, booked: ny.stBooked, prepaid: ny.stPrepaid, refused: ny.stRefused } as const;

/** Строка квеста свёрнутой карточки: бронь с игроками или «квеста нет» с пожеланиями (число людей — тоже PlayersBadge). */
function QuestLineView({ card, today }: { card: NyCard; today: string }) {
  const q = questLine(card, today);
  if (q.kind === "game") {
    const b = q.booking;
    return (
      <span className={ny.qLine}>
        <b>{b.quest || "—"}</b> · {ddmm(b.date)} · {hm(b)}
        {/* в шапке-кнопке бейдж без bookingId — индикатор (кнопка в кнопке недопустима); карточка игроков — в строке брони */}
        <PlayersBadge count={b.players} size="sm" />
        {q.lead ? <span className={ny.bkTag}>лид</span> : null}
        {b.status === "completed" ? <span className={ny.bkTag}>сыграли</span> : null}
      </span>
    );
  }
  return (
    <span className={`${ny.qLine} ${ny.qNone}`}>
      {wishLine(q)}
      {q.people ? <PlayersBadge count={q.people} size="sm" /> : null}
    </span>
  );
}

type Panel = "notes" | "shots" | null;

export function NyCardView({ card, today, open, scrollTo, onToggle, onOpenBooking, onChanged, onToast }: {
  card: NyCard; today: string; open: boolean; scrollTo: boolean; onToggle: () => void;
  onOpenBooking: (b: NyBooking) => void; onChanged: () => void; onToast: (m: string) => void;
}) {
  /* 04.11: комментарии на виду — раскрыл карточку, лента и поле «Добавить» уже открыты */
  const [panel, setPanel] = useState<Panel>("notes");
  const [info, setInfo] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  /* возврат из модалки брони: раскрытая карточка — снова на экране */
  useEffect(() => { if (scrollTo) ref.current?.scrollIntoView({ block: "start" }); }, [scrollTo]);
  const name = card.name ?? "";
  const title = name || card.phone || "Без имени";
  const hot = groupOf(card, today) === 0;
  const target = infoTarget(card);
  const noRef = card.id == null;
  const pay = cardPay(card);
  const toggle = (p: Panel) => setPanel((x) => (x === p ? null : p));
  const counts = [
    card.bookings.length ? `${card.bookings.length} ${plural(card.bookings.length, "бронь", "брони", "броней")}` : "",
    card.leads.length ? `${card.leads.length} ${plural(card.leads.length, "заявка", "заявки", "заявок")}` : "",
    card.notes ? `комментариев ${card.notes}` : "", card.shots ? `скринов ${card.shots}` : "",
  ].filter(Boolean).join(" · ");

  return (
    <div ref={ref} className={`${ny.card} ${hot ? ny.cardHot : ""} ${open ? ny.cardOpen : ""}`}>
      <button type="button" className={ny.head} onClick={onToggle} aria-expanded={open}>
        <span className={ny.nameRow}>
          <span className={`${ny.name} ${name ? "" : ny.nameDim}`}>{title}</span>
          <span className={`${ny.st} ${ST_CLS[card.status]}`}>{NY_STATUS[card.status]}</span>
          {pay ? <span className={`${ny.pay} ${pay.paid ? ny.payOk : ny.payNo}`}>{pay.text}</span> : null}
          {hot ? <span className={ny.tagNew}>заявка</span> : null}
        </span>
        <span className={`${ny.chev} ${open ? ny.chevOpen : ""}`}><Chev /></span>
        <span className={ny.meta}>
          {name && card.phone ? <span className={ny.phone}>{card.phone}</span> : null}
          <QuestLineView card={card} today={today} />
          {card.last_note ? (
            <span className={ny.lastNote} title={card.last_note.text}>
              <b>{card.last_note.author || "—"}</b>, {fmtStamp(card.last_note.at)}: {card.last_note.text}
            </span>
          ) : null}
          {counts ? <span className={ny.cnt}>{counts}</span> : null}
        </span>
      </button>
      {open ? (
        <div className={ny.body}>
          {card.phone ? (
            <a className={ny.call} href={`tel:${card.phone.replace(/[^\d+]/g, "")}`}>
              <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true"><path d="M2 3a1 1 0 0 1 1-1h1.5l1 2-1 1a6 6 0 0 0 3 3l1-1 2 1V9.5a1 1 0 0 1-1 1A9 9 0 0 1 2 3z" /></svg>
              Позвонить {card.phone}
            </a>
          ) : null}
          <NyStatusBar card={card} onChanged={onChanged} onToast={onToast} />
          {card.bookings.length ? <div className={ny.lbl}>Брони и лиды со слотом<b>{card.bookings.length}</b></div> : null}
          {card.bookings.map((b) => <BookingRow key={b.id} b={b} name={name} onOpen={onOpenBooking} onChanged={onChanged} onToast={onToast} />)}
          {card.leads.length ? <div className={ny.lbl}>Заявки без времени<b>{card.leads.length}</b></div> : null}
          {card.leads.map((l) => <LeadRow key={l.id} l={l} onChanged={onChanged} onToast={onToast} />)}
          <div className={ny.acts}>
            <button type="button" className={`${ny.act} ${panel === "notes" ? ny.actOn : ""}`} disabled={noRef} onClick={() => toggle("notes")}>Комментарии{card.notes ? <b>{card.notes}</b> : null}</button>
            <button type="button" className={`${ny.act} ${panel === "shots" ? ny.actOn : ""}`} disabled={noRef} onClick={() => toggle("shots")}>Скрины{card.shots ? <b>{card.shots}</b> : null}</button>
            <button type="button" className={ny.act} disabled={!target} onClick={() => setInfo(true)} title="История игр клиента">Инфо</button>
          </div>
          {noRef ? <div className={ny.hint}>Нет телефона клиента (или это номер-заглушка) — комментарии и скрины привязать не к кому.</div> : null}
          {card.id != null && panel === "notes" ? <NyNotes cardRef={card.id} onChanged={onChanged} onToast={onToast} /> : null}
          {card.id != null && panel === "shots" ? <NyShots cardRef={card.id} onChanged={onChanged} onToast={onToast} /> : null}
        </div>
      ) : null}
      {info && target ? (
        <ClientInfoCard {...("bookingId" in target ? { bookingId: target.bookingId } : { phone: target.phone })}
          fallbackName={name || undefined} onClose={() => setInfo(false)} />
      ) : null}
    </div>
  );
}
